import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"

export default class extends Controller {
  static targets = ["dropzone", "message", "previewTemplate"]
  static values = {
    url: String,
    maxFiles: Number,
    maxFileSize: { type: Number, default: 10 },
    acceptedFiles: String,
    paramName: { type: String, default: "file" },
    autoProcessQueue: { type: Boolean, default: true },
    addRemoveLinks: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false }
  }

  connect() {
    Dropzone.autoDiscover = false
    this.initDropzone()
  }

  disconnect() {
    if (this.dropzone) {
      this.dropzone.destroy()
    }
  }

  initDropzone() {
    if (!this.hasDropzoneTarget) return

    const options = {
      url: this.urlValue,
      paramName: this.paramNameValue,
      maxFilesize: this.maxFileSizeValue,
      autoProcessQueue: this.autoProcessQueueValue,
      addRemoveLinks: this.addRemoveLinksValue,
      clickable: true,
      createImageThumbnails: true,
      thumbnailWidth: 120,
      thumbnailHeight: 120,
      dictDefaultMessage: "",
      dictRemoveFile: "Remove",
      dictCancelUpload: "Cancel",
      dictFileTooBig: "File is too big ({{filesize}}MB). Max filesize: {{maxFilesize}}MB.",
      dictInvalidFileType: "You can't upload files of this type.",
      dictMaxFilesExceeded: "You can not upload any more files."
    }

    if (this.hasMaxFilesValue) {
      options.maxFiles = this.maxFilesValue
    }

    if (this.hasAcceptedFilesValue) {
      options.acceptedFiles = this.acceptedFilesValue
    }

    if (this.hasPreviewTemplateTarget) {
      options.previewTemplate = this.previewTemplateTarget.innerHTML
    }

    this.dropzone = new Dropzone(this.dropzoneTarget, options)

    if (this.disabledValue) {
      this.disable()
    }

    this.bindEvents()
  }

  bindEvents() {
    this.dropzone.on("addedfile", (file) => {
      this.hideMessage()
      this.dispatch("addedfile", { detail: { file } })
    })

    this.dropzone.on("removedfile", (file) => {
      if (this.dropzone.files.length === 0) {
        this.showMessage()
      }
      this.dispatch("removedfile", { detail: { file } })
    })

    this.dropzone.on("success", (file, response) => {
      this.dispatch("success", { detail: { file, response } })
    })

    this.dropzone.on("error", (file, errorMessage) => {
      this.dispatch("error", { detail: { file, errorMessage } })
    })

    this.dropzone.on("complete", (file) => {
      this.dispatch("complete", { detail: { file } })
    })

    this.dropzone.on("uploadprogress", (file, progress) => {
      this.dispatch("uploadprogress", { detail: { file, progress } })
    })

    this.dropzone.on("sending", (file, xhr, formData) => {
      this.dispatch("sending", { detail: { file, xhr, formData } })
    })

    this.dropzone.on("queuecomplete", () => {
      this.dispatch("queuecomplete")
    })

    this.dropzone.on("maxfilesexceeded", (file) => {
      this.dispatch("maxfilesexceeded", { detail: { file } })
    })

    this.dropzone.on("dragenter", () => {
      this.element.classList.add("border-primary")
    })

    this.dropzone.on("dragleave", () => {
      this.element.classList.remove("border-primary")
    })

    this.dropzone.on("drop", () => {
      this.element.classList.remove("border-primary")
    })
  }

  hideMessage() {
    if (this.hasMessageTarget) {
      this.messageTarget.classList.add("hidden")
    }
  }

  showMessage() {
    if (this.hasMessageTarget) {
      this.messageTarget.classList.remove("hidden")
    }
  }

  processQueue() {
    if (this.dropzone) {
      this.dropzone.processQueue()
    }
  }

  removeAllFiles(cancelIfNecessary = true) {
    if (this.dropzone) {
      this.dropzone.removeAllFiles(cancelIfNecessary)
      this.showMessage()
    }
  }

  getAcceptedFiles() {
    if (!this.dropzone) return []
    return this.dropzone.getAcceptedFiles()
  }

  getRejectedFiles() {
    if (!this.dropzone) return []
    return this.dropzone.getRejectedFiles()
  }

  getQueuedFiles() {
    if (!this.dropzone) return []
    return this.dropzone.getQueuedFiles()
  }

  getUploadingFiles() {
    if (!this.dropzone) return []
    return this.dropzone.getUploadingFiles()
  }

  enable() {
    if (this.dropzone) {
      this.dropzone.enable()
      this.element.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  disable() {
    if (this.dropzone) {
      this.dropzone.disable()
      this.element.classList.add("opacity-50", "pointer-events-none")
    }
  }
}
